import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import type { CamposDeLaFila, PlantacionDeInforme } from "@/lib/informes";
import { numeroEscrito } from "@/lib/numeroEscrito";

import { RotuloDeGrupo } from "./CamposDeConstruccion";
import { GrupoDeDatos, GrupoDeIdentificacion, PlantasTotales } from "./CamposDePlantacion";
import { FotosDeLaFila } from "./FotosDeLaFila";
import { camposDesdePlantacion, PLANTACION_VACIA, type FormularioDePlantacion } from "./plantaciones";

interface FormularioDeNuevaPlantacionProps {
  ocupada: boolean;
  /** Las filas del informe: de ahí salen las sugerencias de especie. */
  filas: PlantacionDeInforme[];
  /** Con qué arranca: lo que la App ya sabe de una plantación creada en terreno. */
  inicial?: FormularioDePlantacion;
  /** Crea la fila y, con el id que devuelve, sube las fotos retenidas. */
  onAgregar: (campos: CamposDeLaFila, fotos: File[]) => void;
  onCerrar: () => void;
}

/**
 * Lo que falta para poder agregar, dicho en palabras. El diseño pide ESPECIE Y
 * SUPERFICIE: la especie es la clave que agrupa el cuadro, y sin superficie la
 * fila no suma en el pie.
 */
function loQueFalta(formulario: FormularioDePlantacion): string[] {
  const superficie = numeroEscrito(formulario.superficie_ha);

  return [
    formulario.especie.trim() === "" && "especie",
    (superficie === null || superficie <= 0) && "superficie",
  ].filter((falta): falta is string => falta !== false);
}

/**
 * El formulario EN LÍNEA de la lista: agrega una plantación nueva.
 *
 * SIEMPRE AGREGA, nunca corrige: corregir una fila abre el modal, con su propio
 * estado, así que un borrador a medio escribir acá no se pierde al abrir otra
 * fila. Se desmonta al cerrar, y con él se van el borrador y las fotos
 * retenidas — "Cancelar" promete eso.
 *
 * LAS FOTOS SE RETIENEN en el navegador y viajan con el id que devuelve el
 * alta (`onAgregar`): hasta ahí no hay fila a la cual colgarlas.
 */
export function FormularioDeNuevaPlantacion({
  ocupada,
  filas,
  inicial,
  onAgregar,
  onCerrar,
}: FormularioDeNuevaPlantacionProps) {
  const [formulario, setFormulario] = useState<FormularioDePlantacion>(inicial ?? PLANTACION_VACIA);
  const [fotos, setFotos] = useState<File[]>([]);
  const [rechazoDeFoto, setRechazoDeFoto] = useState<string | null>(null);

  const faltan = loQueFalta(formulario);
  const puedeAgregar = faltan.length === 0;

  function escribir(cambio: Partial<FormularioDePlantacion>) {
    setFormulario((actual) => ({ ...actual, ...cambio }));
  }

  function agregar() {
    if (!puedeAgregar) return;

    onAgregar(camposDesdePlantacion(formulario), fotos);
    onCerrar();
  }

  const comunes = { idPrefijo: "plantacion", formulario, ocupada, escribir };

  return (
    <div
      id="nueva-plantacion"
      className="bg-superficie-sutil border-divisor flex scroll-mt-5 flex-col gap-3.5 border-y-[1.5px] px-5 pt-4 pb-4.5"
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <h4 className="text-ink text-[0.84375rem] font-bold">Nueva plantación</h4>
        <span className="text-ink-muted text-caption">
          {inicial
            ? "Viene de terreno con lo que el encargado anotó. Completa lo que falte; las plantas totales las calcula el sistema al guardar."
            : "Completa al menos especie y superficie. La especie agrupa la lista: escríbela igual que en las otras filas."}
        </span>
      </div>

      <RotuloDeGrupo>Identificación</RotuloDeGrupo>
      <GrupoDeIdentificacion {...comunes} filas={filas} requeridos={["especie", "superficie_ha"]} />

      <RotuloDeGrupo aparte="Opcional al crear">Datos de la plantación</RotuloDeGrupo>
      <GrupoDeDatos {...comunes} />

      <PlantasTotales formulario={formulario} />

      <FotosDeLaFila
        titulo="Fotos de la plantación (una o más)"
        etiquetaDelSelector="Adjuntar fotos de la plantación"
        ocupada={ocupada}
        editando={false}
        pendientes={fotos}
        // Nunca corrige una fila que ya existe: `editando={false}` hace que se
        // dibuje la lista de PENDIENTES y no la de guardadas.
        guardadas={[]}
        urlDe={() => ""}
        alt="Foto de la plantación"
        error={rechazoDeFoto}
        onElegidas={(elegidos) => setFotos((actuales) => [...actuales, ...elegidos])}
        onRechazo={setRechazoDeFoto}
        onQuitarPendiente={(indice) => setFotos((actuales) => actuales.filter((_, i) => i !== indice))}
        onQuitarGuardada={() => {}}
      />

      <div className="flex flex-wrap items-center gap-3 pt-1">
        <Button type="button" onClick={agregar} disabled={ocupada || !puedeAgregar}>
          {ocupada ? <Spinner /> : null}
          Agregar a la lista
        </Button>
        <Button type="button" variant="ghost" onClick={onCerrar}>
          Cancelar
        </Button>
        {puedeAgregar ? null : (
          <span className="text-ink-hint text-caption">Falta: {faltan.join(" y ")}</span>
        )}
      </div>
    </div>
  );
}
